import { sameContent, type WorkspaceDocument } from '../workspace/model/workspace-document';

const STORAGE_PREFIX = 'aibi.report-pub.';
const CHANGE_EVENT = 'aibi:report-publication-changed';

/** Ultima publicacion conocida de un analisis, para saber si hay algo nuevo que publicar. */
export interface ReportPublicationSnapshot {
  readonly reportId: string;
  readonly name: string;
  readonly description: string;
  readonly semanticModelId: string;
  readonly cards: unknown;
}

const storageKey = (workspaceId: string): string => `${STORAGE_PREFIX}${workspaceId}`;

const isSnapshot = (value: unknown): value is ReportPublicationSnapshot => {
  if (value === null || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.reportId === 'string' &&
    typeof record.name === 'string' &&
    typeof record.description === 'string' &&
    typeof record.semanticModelId === 'string' &&
    'cards' in record
  );
};

export function readPublicationSnapshot(
  storage: Pick<Storage, 'getItem'>,
  workspaceId: string,
): ReportPublicationSnapshot | null {
  const raw = storage.getItem(storageKey(workspaceId));
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isSnapshot(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function writePublicationSnapshot(
  storage: Pick<Storage, 'setItem'>,
  workspaceId: string,
  snapshot: ReportPublicationSnapshot,
): void {
  storage.setItem(storageKey(workspaceId), JSON.stringify(snapshot));
  notifyPublicationChanged(workspaceId);
}

export function clearPublicationSnapshot(
  storage: Pick<Storage, 'removeItem'>,
  workspaceId: string,
): void {
  storage.removeItem(storageKey(workspaceId));
  notifyPublicationChanged(workspaceId);
}

const notifyPublicationChanged = (workspaceId: string): void => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(CHANGE_EVENT, { detail: { workspaceId } satisfies { workspaceId: string } }),
  );
};

/** Hay algo que publicar: nunca se publico, o el lienzo ya no coincide con lo publicado. */
export function hasPublishableChanges(
  document: WorkspaceDocument,
  snapshot: ReportPublicationSnapshot | null,
): boolean {
  if (document.cards.length === 0) return false;
  if (snapshot === null) return true;
  return !sameContent(
    { semanticModelId: document.semanticModelId, cards: document.cards },
    { semanticModelId: snapshot.semanticModelId, cards: snapshot.cards },
  );
}

/** El formulario de publicacion difiere del ultimo snapshot (titulo, descripcion o lienzo). */
export function publicationFormChanged(
  document: WorkspaceDocument,
  meta: { readonly name: string; readonly description: string },
  snapshot: ReportPublicationSnapshot | null,
): boolean {
  if (snapshot === null) return document.cards.length > 0;
  const name = meta.name.trim();
  const description = meta.description.trim();
  if (name !== snapshot.name || description !== snapshot.description) return true;
  return hasPublishableChanges(document, snapshot);
}

export function subscribePublicationChanges(workspaceId: string, onChange: () => void): () => void {
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<{ workspaceId: string }>).detail;
    if (detail.workspaceId === workspaceId) onChange();
  };
  const onStorage = (event: StorageEvent) => {
    if (event.key === storageKey(workspaceId)) onChange();
  };
  window.addEventListener(CHANGE_EVENT, handler);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, handler);
    window.removeEventListener('storage', onStorage);
  };
}
